import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { SurvivorClass } from '../systems/CampManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ALL_RESOURCES, RESOURCE_INFO } from '../systems/ResourceManager';
import { BUILDING_ORDER, BUILDING_DEFINITIONS } from '../data/buildings';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import { EXPEDITION_DEFINITIONS } from '../data/expeditions';
import { DisplayModeSettings, DisplayMode } from '../utils/DisplayModeSettings';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { LifetimeStats, formatPlayTime } from '../systems/LifetimeStats';
import { BugReportManager } from '../systems/BugReportManager';
import { downloadAllBugReportsAsMarkdown } from '../systems/BugReportExport';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE DEV / DEBUG ===
// Accessible via F1 depuis n'importe quelle scène.

const BTN_STYLE = {
  fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
  backgroundColor: '#1e2d4a', padding: { x: 10, y: 5 },
};

const HEADER_H = 45;

export class DevScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private run!: RunManager;

  constructor() {
    super({ key: 'DevScene' });
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Fond semi-transparent
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.92).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x000000).setDepth(10);
    this.add.text(GAME_CX, 12, '🛠️ DEV MODE — F1/ESC pour fermer', {
      fontSize: '16px', color: '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Fermer avec F1/ESC
    this.input.keyboard!.on('keydown-F1', () => this.scene.stop('DevScene'));
    this.input.keyboard!.on('keydown-ESC', () => this.scene.stop('DevScene'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 10;

    // --- STATS JOUEUR ---
    y = this.section(y, '--- STATS JOUEUR ---');

    const hp = this.run.getPlayerHp();
    const gold = this.run.getGold();
    const camp = this.run.getCamp();
    const survivors = camp.getAliveSurvivors();

    this.panel.add(this.add.text(130, y, `❤️ HP: ${hp > 0 ? hp : 'non init'}   💰 Or: ${gold}   👥 Survivants: ${survivors.length}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));
    y += 25;

    // --- MODIFIER ---
    y = this.section(y, '--- MODIFIER ---');

    y += this.btnRow(y, [
      ['❤️ HP=Max', () => { this.run.setPlayerHp(20); this.refreshScene(); }],
      ['❤️ HP=5', () => { this.run.setPlayerHp(5); this.refreshScene(); }],
      ['❤️ HP=1', () => { this.run.setPlayerHp(1); this.refreshScene(); }],
      ['💰 +50', () => { this.run.addGold(50); this.refreshScene(); }],
      ['💰 +200', () => { this.run.addGold(200); this.refreshScene(); }],
    ]);

    // --- SURVIVANTS ---
    y = this.section(y, `--- SURVIVANTS (${survivors.length}) ---`);

    const classes: SurvivorClass[] = ['warrior', 'ranger', 'medic'];
    const survivorBtns: Array<[string, () => void]> = classes.map(cls => {
      const emoji = cls === 'warrior' ? '⚔️' : cls === 'ranger' ? '🏹' : '💊';
      return [`${emoji} +${cls}`, () => { camp.addSurvivor(cls); this.refreshScene(); }];
    });
    survivorBtns.push(['🗑️ Vider', () => { camp.resetAll(); this.refreshScene(); }]);
    y += this.btnRow(y, survivorBtns);

    // Reset des presets d'équipement (v0.38.15)
    y += this.btnRow(y, [
      ['💼 Reset presets', () => {
        try { localStorage.removeItem('deadtide_equipment_presets'); } catch { /* */ }
        this.refreshScene();
      }],
    ]);

    // Liste actuelle
    if (survivors.length > 0) {
      for (const s of survivors) {
        const emoji = s.class === 'warrior' ? '⚔️' : s.class === 'ranger' ? '🏹' : '💊';
        this.panel.add(this.add.text(140, y, `${emoji} ${s.name} (${s.class})`, {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }));
        y += 18;
      }
    }
    y += 10;

    // --- SCÈNES ---
    y = this.section(y, '--- SCÈNES ---');

    y += this.btnRow(y, [
      ['🗺️ Carte', () => this.goTo('MapScene')],
      ['⚔️ Combat', () => this.goTo('CombatScene')],
      ['💀 Élite', () => { this.selectEliteNode(); this.goTo('CombatScene'); }],
      ['👹 Boss', () => { this.selectBossNode(); this.goTo('CombatScene'); }],
    ]);

    y += this.btnRow(y, [
      ['🎁 Loot', () => this.goTo('LootScene')],
      ['⚓ Équip', () => this.goTo('EquipScene')],
      ['❓ Event', () => this.goTo('EventScene')],
      ['🏪 Shop', () => this.goTo('ShopScene')],
    ]);

    y += this.btnRow(y, [
      ['🔍 Explore', () => this.goTo('ExploreScene')],
      ['👤 Survivant', () => this.goTo('SurvivorScene')],
      ['⛵ Extract', () => this.goTo('ExtractScene')],
      ['🏆 Victoire', () => this.goTo('RunVictoryScene')],
    ]);

    y += this.btnRow(y, [
      ['📤 Fin Expé (V)', () => { this.stopAll(); startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: true }); }],
      ['📤 Fin Expé (D)', () => { this.stopAll(); startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: false }); }],
      ['⚓ Prépa', () => { this.stopAll(); startSceneWithFade(this, 'RunPrepScene', { camp, meta: this.run.getMeta() }); }],
      ['🏆 Classement', () => { this.stopAll(); startSceneWithFade(this, 'LeaderboardScene', { returnTo: 'BootScene' }); }],
    ]);

    // --- CAMP ---
    y = this.section(y, '--- CAMP ---');

    y += this.btnRow(y, [
      ['🏴‍☠️ Camp', () => { this.stopAll(); startSceneWithFade(this, 'CampScene', { camp, meta: this.run.getMeta() }); }],
      ['📦 Coffre', () => { this.stopAll(); startSceneWithFade(this, 'StorageScene', { camp, meta: this.run.getMeta() }); }],
      ['🛏️ Dortoir', () => { this.stopAll(); startSceneWithFade(this, 'DormScene', { camp, meta: this.run.getMeta() }); }],
      ['📊 Stats', () => { this.stopAll(); startSceneWithFade(this, 'StatsScene', { meta: this.run.getMeta() }); }],
    ]);

    y += this.btnRow(y, [
      ['🔨 Atelier', () => { this.stopAll(); startSceneWithFade(this, 'AtelierScene', { camp, meta: this.run.getMeta() }); }],
      ['🗼 Vigie', () => { this.stopAll(); startSceneWithFade(this, 'VigieScene', { camp, meta: this.run.getMeta() }); }],
    ]);

    // Phase 10 — accès rapide aux nouvelles scènes méta + reset popup quoi-de-neuf
    y += this.btnRow(y, [
      ['📜 Roadmap', () => { this.stopAll(); startSceneWithFade(this, 'RoadmapScene', { returnTo: 'BootScene' }); }],
      ['📋 Patchnotes', () => { this.stopAll(); startSceneWithFade(this, 'PatchNotesScene', { returnTo: 'BootScene' }); }],
      ['🆕 Reset popup quoi-de-neuf', () => {
        try { localStorage.removeItem('deadtide_last_seen_version'); } catch { /* */ }
        this.refreshScene();
      }],
    ]);

    // Refonte île pixel art (Sprints A1+A2+B1+A3+A4) — flag activé par défaut depuis v0.40.6.
    // Le toggle reste pour repasser ponctuellement sur l'ancien CampScene (debug/comparaison).
    // L'ancien camp reste accessible via le panneau d'affichage de l'île (escape hatch).
    const islandFlag = IslandCampFlag.isEnabled();
    const aliveCount = camp.getAliveSurvivors().length;
    y = this.section(y, `--- ÎLE PIXEL ART (A1+A2+B1+A3+A4 ${islandFlag ? '✅ ACTIVÉE (défaut)' : '❌ désactivée — ancien camp forcé'}) — ${aliveCount} survivant(s) vivant(s) ---`);
    y += this.btnRow(y, [
      [`${islandFlag ? '📜 Forcer ancien camp' : '🏝️ Restaurer île'}`, () => {
        IslandCampFlag.toggle();
        this.refreshScene();
      }],
      ['🏝️ Aller à l\'île', () => {
        this.stopAll();
        startSceneWithFade(this, 'IslandCampScene', { camp, meta: this.run.getMeta() });
      }],
    ]);
    y += this.btnRow(y, [
      ['👥 Recruter 3 survivants test', () => {
        camp.addSurvivor('warrior');
        camp.addSurvivor('ranger');
        camp.addSurvivor('medic');
        this.refreshScene();
      }],
      ['💀 Tuer tous les survivants', () => {
        for (const s of camp.getAliveSurvivors()) camp.killSurvivor(s.id);
        this.refreshScene();
      }],
    ]);
    y += this.btnRow(y, [
      ['⬆️ Lvl up tous les survivants', () => {
        // Lvl up jusqu'à 5 fois pour atteindre le cap depuis n'importe quel niveau.
        const ids = camp.getAliveSurvivors().map(s => s.id);
        for (let i = 0; i < 5; i++) camp.levelUpSurvivors(ids);
        this.refreshScene();
      }],
    ]);

    // --- EXPÉDITIONS IDLE ---
    y = this.section(y, '--- EXPÉDITIONS IDLE ---');

    const expMgr = new ExpeditionManager();
    const vigieLevel = camp.getUpgrades().getBuildingLevel('watchtower');
    const activeExp = expMgr.getActive();
    const expStatus = activeExp
      ? `${EXPEDITION_DEFINITIONS[activeExp.expeditionId].emoji} ${EXPEDITION_DEFINITIONS[activeExp.expeditionId].name} (${activeExp.survivorIds.length} surv.)`
      : `Vigie niv.${vigieLevel} — Aucune en cours`;
    this.panel.add(this.add.text(130, y, expStatus, { fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY }));
    y += 20;

    y += this.btnRow(y, [
      ['🏖️ Lancer côtière', () => {
        const survivors = camp.getAliveSurvivors();
        if (survivors.length > 0 && !expMgr.isOnExpedition()) {
          expMgr.startExpedition('coastal', [survivors[0].id]);
        }
        this.refreshScene();
      }],
      ['📋 Résoudre', () => {
        if (expMgr.isOnExpedition()) {
          const result = expMgr.resolveExpedition(camp.getSurvivors());
          if (result) {
            this.stopAll();
            startSceneWithFade(this, 'ExpeditionResultScene', { run: this.run, result, victory: true });
          }
        }
      }],
      ['🗑️ Reset Expé', () => { expMgr.resetAll(); this.refreshScene(); }],
    ]);

    // --- COMBAT RAPIDE ---
    y = this.section(y, '--- COMBAT RAPIDE ---');

    y += this.btnRow(y, [
      ['⚡ Auto-Win', () => {
        if (this.run.getPlayerHp() <= 0) this.run.setPlayerHp(20);
        this.run.addGold(8);
        this.run.completeCurrentNode();
        this.stopAll();
        startSceneWithFade(this, 'LootScene', { run: this.run });
      }],
      ['💀 Défaite', () => {
        this.stopAll();
        startSceneWithFade(this, 'MapScene', { run: new RunManager(this.run.getMeta(), camp) });
      }],
    ]);

    // --- RESSOURCES ---
    y = this.section(y, '--- RESSOURCES ---');

    const res = camp.getResources();
    const stock = res.getAll();
    const resLabel = ALL_RESOURCES.map(t => `${RESOURCE_INFO[t].emoji}${stock[t]}`).join('  ');
    this.panel.add(this.add.text(130, y, resLabel, { fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY }));
    y += 20;

    y += this.btnRow(y, [
      ['🪵+10 chaque', () => { for (const t of ALL_RESOURCES) res.add(t, 10); this.refreshScene(); }],
      ['🗑️ Reset Ressources', () => { res.resetAll(); this.refreshScene(); }],
    ]);

    // --- BÂTIMENTS ---
    y = this.section(y, '--- BÂTIMENTS ---');

    const upgrades = camp.getUpgrades();
    const levels = upgrades.getAllLevels();
    const levelsLabel = BUILDING_ORDER.map(id => `${BUILDING_DEFINITIONS[id].emoji}${levels[id]}`).join('  ');
    this.panel.add(this.add.text(130, y, levelsLabel, { fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY }));
    y += 20;

    y += this.btnRow(y, [
      ['⬆️ Tous Niv.3', () => {
        for (const id of BUILDING_ORDER) {
          while (upgrades.getBuildingLevel(id) < 3) {
            if (!upgrades.startConstruction(id, res)) break;
            upgrades.completeConstruction();
          }
        }
        this.refreshScene();
      }],
      ['🗑️ Reset Bâtiments', () => { upgrades.resetAll(); this.refreshScene(); }],
    ]);

    // --- MODE D'AFFICHAGE ---
    y = this.section(y, `--- MODE D'AFFICHAGE (actuel : ${DisplayModeSettings.getMode()}) ---`);
    const switchMode = (mode: DisplayMode) => {
      DisplayModeSettings.setMode(mode);
      try { window.location.reload(); } catch { /* */ }
    };
    y += this.btnRow(y, [
      ['📱 Auto', () => switchMode('auto')],
      ['📱 Mobile', () => switchMode('mobile')],
      ['🖥️ Bureau', () => switchMode('desktop')],
    ]);

    // --- LIFETIME STATS (debug brut) ---
    y = this.section(y, '--- LIFETIME STATS ---');

    const lifetime = LifetimeStats.getAll();
    const mostPlayed = LifetimeStats.getMostPlayedClass();
    const lifetimeLines = [
      `⏱️ ${formatPlayTime(lifetime.totalPlayTimeMs)}   📦 Ressources cumulées: ${lifetime.resourcesTotal}`,
      `🎭 ${mostPlayed ? `${mostPlayed.cls} ×${mostPlayed.count}` : 'aucune classe'}   ⚔️${lifetime.classesPlayed.warrior} 🏹${lifetime.classesPlayed.ranger} 💊${lifetime.classesPlayed.medic}`,
      `📅 Première session: ${lifetime.firstSeenAt ? lifetime.firstSeenAt.slice(0, 10) : '—'}`,
    ];
    for (const line of lifetimeLines) {
      this.panel.add(this.add.text(130, y, line, { fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY }));
      y += 16;
    }
    y += 4;
    y += this.btnRow(y, [
      ['⏱️ +1h', () => { LifetimeStats.tick(60 * 60 * 1000); this.refreshScene(); }],
      ['📦 +10 ressources', () => {
        for (const t of ALL_RESOURCES) LifetimeStats.addResource(t, 2);
        this.refreshScene();
      }],
      ['🗑️ Reset Lifetime', () => { LifetimeStats.resetAll(); this.refreshScene(); }],
    ]);

    // --- META STATS (debug brut) ---
    y = this.section(y, '--- META STATS (raw) ---');

    const metaStats = this.run.getMeta().getStats();
    const metaLines = [
      `🏴‍☠️ Runs: ${metaStats.totalRuns} (W${metaStats.runsWon}/L${metaStats.runsLost})   ⚔️ Kills: ${metaStats.enemiesKilled}   👹 Boss: ${metaStats.bossesKilled}`,
      `💰 Or: +${metaStats.totalGoldEarned} / -${metaStats.totalGoldSpent}   🎒 Loots: ${metaStats.itemsLooted}   🗺️ Nœuds: ${metaStats.nodesVisited}`,
      `❤️ Soin: ${metaStats.healingReceived}   ⚔️ Dég.donnés: ${metaStats.damageDealt}   🩸 Dég.subis: ${metaStats.damageTaken}`,
      `🔪 Best run kills: ${metaStats.bestRunKills}   💎 Best run or: ${metaStats.bestRunGold}   ⛰️ Étage max: ${metaStats.floorsReached}`,
    ];
    for (const line of metaLines) {
      this.panel.add(this.add.text(130, y, line, { fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY }));
      y += 16;
    }
    y += 8;

    // --- CRASH RECOVERY (test) ---
    y = this.section(y, '--- CRASH RECOVERY (test du filet de sécurité) ---');

    // Lance les erreurs hors du callback Phaser (setTimeout) pour que window.error
    // les attrape — sinon Phaser les avale dans son scope d'event handler.
    y += this.btnRow(y, [
      ['💥 Throw sync', () => {
        this.scene.stop('DevScene');
        setTimeout(() => { throw new Error('Crash de test (sync) — déclenché depuis le mode Dev'); }, 0);
      }],
      ['💥 Throw async', () => {
        this.scene.stop('DevScene');
        setTimeout(() => {
          // Promise rejetée sans .catch → unhandledrejection
          Promise.reject(new Error('Crash de test (async) — Promise rejetée'));
        }, 0);
      }],
    ]);

    // --- BUG REPORTS (export local pour Claude) ---
    // Permet d'exporter l'historique localStorage en un fichier .md auto-suffisant
    // que je peux coller dans une session Claude pour qu'elle bosse dessus
    // sans devoir aller chercher chaque rapport sur Discord à la main.
    const bugReports = BugReportManager.getAll();
    const sentCount = bugReports.filter(r => r.sent).length;
    y = this.section(y, `--- BUG REPORTS LOCAUX (${bugReports.length}) ---`);
    this.panel.add(this.add.text(130, y, `📡 Envoyés Discord: ${sentCount}   💾 Local seul: ${bugReports.length - sentCount}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));
    y += 20;
    y += this.btnRow(y, [
      ['📥 Exporter (MD)', () => { downloadAllBugReportsAsMarkdown(); }],
      ['🗑️ Effacer l\'historique', () => { BugReportManager.clearAll(); this.refreshScene(); }],
    ]);

    // --- RESET ---
    y = this.section(y, '--- RESET ---');

    y += this.btnRow(y, [
      ['🗑️ Stats & Succès', () => { this.run.getMeta().resetAll(); this.refreshScene(); }],
      ['🗑️ Camp complet', () => { camp.resetAll(); this.refreshScene(); }],
      ['🔄 Nouvelle Expé', () => {
        this.stopAll();
        startSceneWithFade(this, 'MapScene', { run: new RunManager(this.run.getMeta(), camp) });
      }],
    ]);

    y += 10;
    this.panel.setContentHeight(y);
  }

  // === HELPERS ===

  private section(y: number, title: string): number {
    this.panel.add(this.add.text(130, y, title, {
      fontSize: '12px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    return y + 22;
  }

  /**
   * Place une rangée de boutons en flow layout, wrap auto si ça dépasse `GAME_W - margin`.
   * Sur desktop (800px) la rangée tient en général sur une ligne, sur mobile (450px) elle wrap.
   * Retourne la hauteur totale utilisée (à ajouter à `y`).
   */
  private btnRow(y: number, buttons: Array<[string, () => void]>): number {
    const startX = 130;
    const gap = 6;
    const margin = 10;
    let curX = startX;
    let curY = y;
    let rowH = 0;

    for (const [label, cb] of buttons) {
      const btn = this.add.text(curX, curY, label, BTN_STYLE);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', cb);
      btn.on('pointerover', () => btn.setColor('#f1c40f'));
      btn.on('pointerout', () => btn.setColor('#ecf0f1'));
      this.panel.add(btn);

      const w = btn.width;
      const h = btn.height;

      // Wrap si on dépasse — sauf si c'est le 1er btn de la rangée (sinon boucle infinie)
      if (curX !== startX && curX + w > GAME_W - margin) {
        curY += rowH + 4;
        curX = startX;
        btn.setPosition(curX, curY);
        rowH = h;
      } else {
        rowH = Math.max(rowH, h);
      }

      curX += w + gap;
    }

    return (curY - y) + rowH + 8;
  }

  private goTo(scene: string): void {
    if (this.run.getPlayerHp() <= 0) this.run.setPlayerHp(20);
    this.ensureNodeSelected();
    this.stopAll();
    startSceneWithFade(this, scene, { run: this.run });
  }

  private stopAll(): void {
    for (const s of this.scene.manager.getScenes(false)) {
      if (s.scene.key !== 'DevScene') this.scene.stop(s.scene.key);
    }
    this.scene.stop('DevScene');
  }

  private ensureNodeSelected(): void {
    const map = this.run.getMap();
    if (map.currentNodeId) return;
    const available = map.nodes.find(n => n.status === 'available');
    if (available) this.run.selectMapNode(available.id);
  }

  private selectEliteNode(): void {
    const map = this.run.getMap();
    const elite = map.nodes.find(n => n.type === 'elite');
    if (elite) { elite.status = 'available'; this.run.selectMapNode(elite.id); }
  }

  private selectBossNode(): void {
    const map = this.run.getMap();
    const boss = map.nodes.find(n => n.type === 'boss');
    if (boss) { boss.status = 'available'; this.run.selectMapNode(boss.id); }
  }

  private refreshScene(): void {
    this.scene.restart({ run: this.run });
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
